import React from 'react';
import { Formik, Field } from 'formik';
import styled from 'styled-components/macro';
import * as Yup from 'yup';
import { Checkbox, Button } from '@deeptrue-frontend/ui';
import { useFormatMessage } from 'components/IntlProvider';
import { FormatMessage } from 'components/IntlProvider/hooks/hooks';
import ErrorMessages from 'components/Fields/ErrorMessages';
import VoucherInfo from 'components/Project/CollectData/VoucherInfo';

export interface FormValues {
  termsAndConditions: boolean;
  understandTerms: boolean;
  non_field_errors?: any;
}

interface Props {
  startDataCollection: any;
  initialValues: FormValues;
  totalFee: number;
  limits: number;
  voucherInfo: any;
}

const getValidationSchema = (t: FormatMessage) =>
  Yup.object().shape<FormValues>({
    termsAndConditions: Yup.bool().required(
      t('errors.fields.required', 'Field is required')
    ),
    understandTerms: Yup.bool().required(
      t('errors.fields.required', 'Field is required')
    ),
  });

const FieldBox = styled.div`
  margin: 1.5rem 0;
  display: flex;
  align-items: center;
  span {
    font-size: 0.875rem;
    margin-left: 0.75rem;
  }
`;

const AutomaticDataCollect = ({
  startDataCollection,
  initialValues,
  totalFee,
  limits,
  voucherInfo,
}: Props) => {
  const t = useFormatMessage();

  const isVoucher = !!(voucherInfo && voucherInfo.voucher);

  const projectPrice = isVoucher
    ? totalFee + voucherInfo.claimed_value
    : totalFee;
  const projectPriceBeforeDiscount = totalFee;
  const getTermsLabel = () => {
    return (
      <>
        {t(
          'project.collectData.sendInvitations.acceptTermsAndConditions',
          'I accept'
        )}{' '}
        <a
          href={t(
            'project.collectData.linkModal.termsAndConditions.link',
            'https://deeptrue.com/de/terms-and-conditions'
          )}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t(
            'project.collectData.linkModal.termsAndConditions.label',
            'Terms & Conditions'
          )}
        </a>
      </>
    );
  };

  const isExceededBudget = totalFee > limits;
  return (
    <Formik
      validationSchema={getValidationSchema(t)}
      onSubmit={startDataCollection}
      initialValues={initialValues}
    >
      {({ handleSubmit, isSubmitting, errors, touched, values }) => {
        const { termsAndConditions, understandTerms } = values;
        const isBothConditionsAccepted = termsAndConditions && understandTerms;
        const nonFieldErrors = errors.non_field_errors;
        return (
          <form onSubmit={handleSubmit}>
            <FieldBox>
              <Field
                name="termsAndConditions"
                as={Checkbox}
                hasError={
                  touched.termsAndConditions && errors.termsAndConditions
                }
                helperText={
                  touched.termsAndConditions && errors.termsAndConditions
                }
                label={getTermsLabel()}
              />
            </FieldBox>
            <FieldBox>
              <Field
                name="understandTerms"
                as={Checkbox}
                hasError={touched.understandTerms && errors.understandTerms}
                helperText={touched.understandTerms && errors.understandTerms}
                label={t(
                  'project.collectData.automatic.understand',
                  'I understand that the project will be executed automatically and can not be altered while running.'
                )}
              />
            </FieldBox>
            {isVoucher && (
              <VoucherInfo
                discountPercent={voucherInfo.voucher.discount_percent}
                projectPrice={projectPrice}
                projectPriceBeforeDiscount={projectPriceBeforeDiscount}
                margin="1rem 0 2rem;"
              />
            )}
            <Button
              disabled={!isBothConditionsAccepted || isExceededBudget}
              isLoading={isSubmitting}
              variant="primary"
              type="submit"
            >
              {t(
                'project.collectData.automatic.submit',
                'Start for {totalFee} credits',
                { totalFee: totalFee }
              )}
            </Button>
            {nonFieldErrors && <ErrorMessages errors={nonFieldErrors} />}
          </form>
        );
      }}
    </Formik>
  );
};

export default AutomaticDataCollect;
